import { SIZES, SPACING } from '../utils/constants';

/**
 * Calculate sticky note height based on text content
 * Matches the calculation from StickyNote component exactly
 */
export function calculateStickyHeight(
  text: string,
  width: number,
  fontSize: number,
  padding: number
): number {
  // Approximate characters per line based on width and font size
  // Be more conservative with character width to account for variable character widths
  const availableWidth = width - padding * 2;
  const avgCharWidth = fontSize * 0.55; // More conservative estimate (reduced from 0.6)
  const charsPerLine = Math.floor(availableWidth / avgCharWidth);
  
  // Split text by newlines to count explicit line breaks
  const textLines = text.split('\n');
  
  // Calculate number of lines needed, accounting for both \n and wrapping
  let totalLines = 0;
  textLines.forEach(line => {
    if (line.length === 0) {
      // Empty line (just \n)
      totalLines += 1;
    } else {
      // Calculate wrapped lines for this text segment
      const wrappedLines = Math.ceil(line.length / charsPerLine);
      totalLines += wrappedLines;
    }
  });
  
  // Calculate height: lines * lineHeight + padding + extra space
  // CanvasText uses lineHeight of 1.4 by default (lineHeight || 1.4)
  // But StickyNote calculates with 1.5. We need to match what CanvasText actually renders.
  // Use 1.4 to match CanvasText's default, but add extra buffer for safety
  const lineHeightMultiplier = 1.5; // Use 1.5 like StickyNote for calculation
  const lineHeight = fontSize * lineHeightMultiplier;
  const contentHeight = totalLines * lineHeight;
  
  // CanvasText positions text at y + padding (top padding)
  // We need: top padding + content height + bottom padding
  // StickyNote uses: contentHeight + padding + SPACING.STICKY_TEXT_EXTRA_SPACE
  // But this seems insufficient. Add padding * 2 for proper top and bottom spacing
  // Add extra buffer (8px) to prevent text cutoff at the bottom
  const totalHeight = contentHeight + padding * 2 + SPACING.STICKY_TEXT_EXTRA_SPACE + 8;
  
  // Return max of calculated height and minimum height
  return Math.max(SIZES.STICKY_NOTE_MIN_HEIGHT, totalHeight);
}

/**
 * Estimate text height for wrapped text
 * Similar to rtlTextRenderer.measureWrappedText but without DOM dependency
 */
export function estimateTextHeight(
  text: string,
  maxWidth: number,
  fontSize: number,
  lineHeight: number = 1.4
): number {
  if (!text || maxWidth <= 0) {
    return fontSize * lineHeight;
  }
  
  const avgCharWidth = fontSize * 0.6;
  const charsPerLine = Math.floor(maxWidth / avgCharWidth);
  const lines = Math.ceil(text.length / charsPerLine);
  return lines * fontSize * lineHeight;
}
